// apps/web/src/pages/auth/VerifyEmail.tsx
//
// Plan 04-12b Task 1 — email verification landing page.
//
// MOUNTED AT TWO PATHS:
//   /auth/verify-email/:token  — user clicked the verification link from email
//   /auth/verify-email/pending — Sign-up redirect destination; tells the user
//                                 to check their inbox
//
// FLOW (token path):
//   1. Extract token from URL
//   2. POST /api/auth/verify-email (Better-Auth's built-in endpoint)
//   3. On success → link to /auth/sign-in
//   4. On error → show error message
//
// FLOW (pending path):
//   Simple informational view — no API call.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { api, ApiError } from "@/lib/api-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type VerifyState = "pending" | "verifying" | "success" | "error";

export function VerifyEmail(): React.ReactElement {
  const params = useParams<{ token?: string }>();
  const token = params.token;

  const [state, setState] = useState<VerifyState>(
    !token || token === "pending" ? "pending" : "verifying",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token || token === "pending") return;

    let cancelled = false;
    void (async () => {
      try {
        // Better-Auth's verify-email endpoint is a GET with token query.
        // We use the api client (which sends credentials: include) — Better-
        // Auth issues the session cookie on successful verification.
        await api(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
          method: "GET",
        });
        if (!cancelled) setState("success");
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setErrorMessage(
            err.status === 410
              ? "This verification link has expired. Please request a new one."
              : err.message,
          );
        } else {
          setErrorMessage(
            err instanceof Error ? err.message : "Could not verify your email",
          );
        }
        setState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Verify your email</CardTitle>
          <CardDescription>
            {state === "pending"
              ? "Check your inbox for a verification link."
              : state === "verifying"
                ? "Verifying your email…"
                : state === "success"
                  ? "Email verified."
                  : "Verification failed."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === "pending" ? (
            <p className="text-sm text-muted-foreground">
              We sent a verification link to your email. Click the link to
              activate your account. The link expires in 15 minutes.
            </p>
          ) : null}
          {state === "verifying" ? (
            <p className="text-sm text-muted-foreground">
              One moment — confirming your email with the server…
            </p>
          ) : null}
          {state === "success" ? (
            <>
              <Alert>
                <AlertTitle>You're all set</AlertTitle>
                <AlertDescription>
                  Your email is verified. You can now sign in.
                </AlertDescription>
              </Alert>
              <Link
                to="/auth/sign-in"
                className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Continue to sign in
              </Link>
            </>
          ) : null}
          {state === "error" ? (
            <>
              <Alert variant="destructive">
                <AlertTitle>Verification failed</AlertTitle>
                <AlertDescription>
                  {errorMessage ?? "Something went wrong."}
                </AlertDescription>
              </Alert>
              <Link
                to="/auth/sign-in"
                className="inline-flex h-10 w-full items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
              >
                Back to sign in
              </Link>
            </>
          ) : null}
        </CardContent>
      </Card>
    </main>
  );
}
